/**
 * Pure helpers for turning raw browser/device signals into human-readable
 * names. Shared by the widget (at capture time) and the feedback drawer (for
 * display and the BrowserStack link), so both agree on the same names.
 */

/**
 * Display names for the browser identifiers `detect-browser` returns. Several
 * of its names are platform-specific aliases (`ios` is Mobile Safari, `crios`
 * is Chrome on iOS, `fxios` is Firefox on iOS), which read as an OS rather
 * than a browser if shown raw.
 */
const BROWSER_DISPLAY_NAMES: Record<string, string> = {
  aol: 'AOL',
  edge: 'Edge',
  'edge-ios': 'Edge',
  'edge-chromium': 'Edge',
  yandexbrowser: 'Yandex',
  kakaotalk: 'KakaoTalk',
  samsung: 'Samsung Internet',
  silk: 'Silk',
  miui: 'MIUI Browser',
  beaker: 'Beaker',
  'chromium-webview': 'Android WebView',
  chrome: 'Chrome',
  crios: 'Chrome',
  phantomjs: 'PhantomJS',
  firefox: 'Firefox',
  fxios: 'Firefox',
  'opera-mini': 'Opera Mini',
  opera: 'Opera',
  pie: 'Pocket IE',
  netfront: 'NetFront',
  ie: 'Internet Explorer',
  bb10: 'BlackBerry',
  android: 'Android Browser',
  ios: 'Safari',
  safari: 'Safari',
  facebook: 'Facebook in-app browser',
  instagram: 'Instagram in-app browser',
  'ios-webview': 'iOS in-app browser',
  curl: 'curl',
  searchbot: 'Search bot',
};

/**
 * The display name for a captured browser identifier. Known `detect-browser`
 * names are mapped (e.g. `ios` → `Safari`); anything else (an already-friendly
 * name, or the AI capture's `chrome/124.0…` form) is returned unchanged.
 */
export function browserDisplayName(rawBrowser: string | null | undefined): string {
  const browser = (rawBrowser ?? '').trim();
  return BROWSER_DISPLAY_NAMES[browser.toLowerCase()] ?? browser;
}

/**
 * Samsung model-number families (the `SM-<letter><3 digits>` prefix, without
 * the regional suffix such as `B`/`U`/`W`) mapped to marketing names.
 */
const SAMSUNG_MODEL_NAMES: Record<string, string> = {
  G990: 'Galaxy S21 FE',
  G991: 'Galaxy S21',
  G996: 'Galaxy S21+',
  G998: 'Galaxy S21 Ultra',
  S901: 'Galaxy S22',
  S906: 'Galaxy S22+',
  S908: 'Galaxy S22 Ultra',
  S711: 'Galaxy S23 FE',
  S911: 'Galaxy S23',
  S916: 'Galaxy S23+',
  S918: 'Galaxy S23 Ultra',
  S721: 'Galaxy S24 FE',
  S921: 'Galaxy S24',
  S926: 'Galaxy S24+',
  S928: 'Galaxy S24 Ultra',
  S731: 'Galaxy S25 FE',
  S931: 'Galaxy S25',
  S936: 'Galaxy S25+',
  S937: 'Galaxy S25 Edge',
  S938: 'Galaxy S25 Ultra',
  F721: 'Galaxy Z Flip4',
  F731: 'Galaxy Z Flip5',
  F741: 'Galaxy Z Flip6',
  F761: 'Galaxy Z Flip7',
  F936: 'Galaxy Z Fold4',
  F946: 'Galaxy Z Fold5',
  F956: 'Galaxy Z Fold6',
  F966: 'Galaxy Z Fold7',
  A146: 'Galaxy A14 5G',
  A155: 'Galaxy A15',
  A156: 'Galaxy A15 5G',
  A165: 'Galaxy A16',
  A166: 'Galaxy A16 5G',
  A256: 'Galaxy A25',
  A346: 'Galaxy A34',
  A356: 'Galaxy A35',
  A366: 'Galaxy A36',
  A546: 'Galaxy A54',
  A556: 'Galaxy A55',
  A566: 'Galaxy A56',
  X710: 'Galaxy Tab S9',
  X716: 'Galaxy Tab S9',
  X810: 'Galaxy Tab S9+',
  X816: 'Galaxy Tab S9+',
  X910: 'Galaxy Tab S9 Ultra',
  X916: 'Galaxy Tab S9 Ultra',
};

/**
 * The marketing name for an Android model string, as reported by User-Agent
 * Client Hints (`model`) or a legacy user agent. Samsung model numbers are
 * mapped (`SM-S911B` → `Samsung Galaxy S23`); Pixels already report a
 * readable name and get the `Google` prefix; other models are returned as-is.
 */
export function androidDeviceName(rawModel: string | null | undefined): string {
  const model = (rawModel ?? '').trim();
  const samsungMatch = model.match(/^SM-([A-Z]\d{3})/i);
  if (samsungMatch) {
    const marketingName = SAMSUNG_MODEL_NAMES[samsungMatch[1].toUpperCase()];
    return marketingName ? `Samsung ${marketingName}` : `Samsung ${model}`;
  }
  if (/^pixel\b/i.test(model)) {
    return `Google ${model}`;
  }
  return model;
}

/**
 * iPhones grouped by portrait screen size in CSS points + device pixel ratio.
 * Apple exposes no model identifier to the web, so models sharing a screen
 * can't be told apart; each group is ordered oldest → newest.
 */
const IPHONE_SCREEN_GROUPS: Array<{ width: number; height: number; pixelRatio: number; models: string[] }> = [
  { width: 320, height: 568, pixelRatio: 2, models: ['iPhone 5s', 'iPhone SE (1st gen)'] },
  {
    width: 375,
    height: 667,
    pixelRatio: 2,
    models: ['iPhone 8', 'iPhone SE (2nd gen)', 'iPhone SE (3rd gen)'],
  },
  { width: 414, height: 736, pixelRatio: 3, models: ['iPhone 8 Plus'] },
  {
    width: 375,
    height: 812,
    pixelRatio: 3,
    models: ['iPhone X', 'iPhone XS', 'iPhone 11 Pro', 'iPhone 12 mini', 'iPhone 13 mini'],
  },
  { width: 414, height: 896, pixelRatio: 2, models: ['iPhone XR', 'iPhone 11'] },
  { width: 414, height: 896, pixelRatio: 3, models: ['iPhone XS Max', 'iPhone 11 Pro Max'] },
  {
    width: 390,
    height: 844,
    pixelRatio: 3,
    models: ['iPhone 12', 'iPhone 12 Pro', 'iPhone 13', 'iPhone 13 Pro', 'iPhone 14', 'iPhone 16e'],
  },
  {
    width: 428,
    height: 926,
    pixelRatio: 3,
    models: ['iPhone 12 Pro Max', 'iPhone 13 Pro Max', 'iPhone 14 Plus'],
  },
  {
    width: 393,
    height: 852,
    pixelRatio: 3,
    models: ['iPhone 14 Pro', 'iPhone 15', 'iPhone 15 Pro', 'iPhone 16'],
  },
  {
    width: 430,
    height: 932,
    pixelRatio: 3,
    models: ['iPhone 14 Pro Max', 'iPhone 15 Plus', 'iPhone 15 Pro Max', 'iPhone 16 Plus'],
  },
  { width: 402, height: 874, pixelRatio: 3, models: ['iPhone 16 Pro', 'iPhone 17', 'iPhone 17 Pro'] },
  { width: 420, height: 912, pixelRatio: 3, models: ['iPhone Air'] },
  { width: 440, height: 956, pixelRatio: 3, models: ['iPhone 16 Pro Max', 'iPhone 17 Pro Max'] },
];

/** Separator between candidate models in a captured device name. */
export const DEVICE_CANDIDATE_SEPARATOR = ' / ';

/**
 * The possible iPhone models for a screen size (`screen.width`/`height` in CSS
 * points, either orientation) and `devicePixelRatio`, oldest → newest. Empty
 * when the size matches no known iPhone (e.g. a model newer than this table).
 */
export function iphoneModelsForScreen(screenWidth: number, screenHeight: number, pixelRatio: number): string[] {
  const shortSide = Math.min(screenWidth, screenHeight);
  const longSide = Math.max(screenWidth, screenHeight);
  const group = IPHONE_SCREEN_GROUPS.find(
    (candidate) =>
      candidate.width === shortSide &&
      candidate.height === longSide &&
      Math.round(pixelRatio) === candidate.pixelRatio,
  );
  return group ? [...group.models] : [];
}
